import type { HelpArticle } from "../../types";
import { STRUCTURAL_HELP_FIXTURES } from "../../../calculations/structuralHelpFixtures";

const beam = STRUCTURAL_HELP_FIXTURES.concrete_beam_aci;
const strip = STRUCTURAL_HELP_FIXTURES.continuous_footing;
const pad = STRUCTURAL_HELP_FIXTURES.pad_footing;
const check = (fixture: typeof beam, key: string) => fixture.checks.find((entry) => entry.limitState === key)!;
const pct = (value: number | null): string => `${((value ?? 0) * 100).toFixed(1)}%`;
const n3 = (value: number | null): string => (value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 3 });

export const concreteFoundationSheetsArticle: HelpArticle = {
  id: "concrete-foundation-sheets",
  title: "Concrete beams & foundations",
  description:
    "Use the manual reinforced-concrete beam, continuous-footing, and interior pad-footing sheets without mixing service and strength demands.",
  tags: ["concrete", "ACI", "beam", "foundation", "footing", "bearing", "punching shear", "reinforcement"],
  lastUpdated: "July 20, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "The Concrete family contains three bounded manual calculators. Concrete Beam checks a singly reinforced rectangular section from entered factored moment and shear. Strip / Continuous Footing and Pad Footing combine service-level soil bearing with strength-level concrete checks. All three resolve concrete and reinforcement by stable database key, then print the resolved properties and exclusions with the result.",
    },

    { kind: "heading", level: 2, text: "Create the right concrete sheet", id: "create" },
    { kind: "where", path: ["Calculations", "Project", "New calculation", "Structural calculators", "Concrete"] },
    {
      kind: "paragraph",
      text: "Select the card that matches the demand model you already have. The beam expects `Mu` and `Vu`; it does not derive them from a span or load list. The strip footing expects wall line loads per foot. The pad footing expects total column loads and is limited in manual mode to the single available `interior` location. Firmark assigns `RCB`, `CF`, or `PF` marks and opens the schema workspace.",
    },
    {
      kind: "shot",
      shotId: "structural-catalog-concrete",
      caption: "The three Concrete cards: one member check and two different foundation demand models.",
    },
    {
      kind: "table",
      caption: "Keep the load levels attached to their fields.",
      headers: ["Sheet", "Service inputs", "Strength inputs / derived demand"],
      rows: [
        ["Concrete Beam", "None", "Factored Mu and Vu entered directly"],
        ["Strip Footing", "D and L wall line loads; one allowable bearing", "Factored strip shear and flexure derived by the engine"],
        ["Pad Footing", "D and L column loads; separate D and D+L allowables", "Factored one-way shear, punching, flexure, reinforcement, bearing"],
      ],
    },

    { kind: "heading", level: 2, text: "Resolve materials before geometry", id: "materials" },
    {
      kind: "paragraph",
      text: "Catalog binding fields are display-only on the sheet; use each binding's Browse action to change the selection. The chosen display name and stable key remain together as the audit record. Concrete Beam exposes the concrete lookup in Materials and the two reinforcement lookups in Section and reinforcement; both footing sheets carry the same three bindings.",
    },
    { kind: "registry-group", calculatorId: "concrete_beam_aci", groupId: "materials" },
    {
      kind: "paragraph",
      text: "The worked fixtures use normalweight 4,000 psi concrete, ASTM A615 Grade 60 reinforcement, and a #8 profile with 0.79 in² nominal area and 1.0 in diameter. These selections are shared across the three examples, but geometry and load fields remain sheet-specific. A select or toggle commits immediately; a number commits when focus leaves it or when Enter moves focus.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Browse is broader than v1 compute",
      body: "The shared database can show lightweight concrete and reinforcement with resolved yield strength above 60,000 psi. All three v1 calculators refuse those selections at recalculation: they require resolved normalweight concrete and reinforcement no stronger than 60,000 psi.",
    },

    { kind: "heading", level: 2, text: "Concrete Beam", id: "beam" },
    {
      kind: "paragraph",
      text: "Enter width, overall depth, clear cover, tension-bar count, maximum aggregate size, stirrup dimensions, and spacing. The v1 section has one tension-bar layer. The aggregate value is not a material label: the runtime uses it with bar diameter and the 1 in minimum to determine the required clear spacing. Loads are factored `Mu` and `Vu`, so this sheet does not publish a support-reaction packet; it has no unfactored span/load decomposition from which to create one.",
    },
    {
      kind: "shot",
      shotId: "concrete-beam-inputs",
      caption: `${beam.mark}: 12 × 24 in, four #8 tension bars, two-leg stirrups at 8 in, Mu 200 kip-ft and Vu 40 kip.`,
    },
    {
      kind: "values",
      caption: `${beam.mark} · engine-authored checks.`,
      rows: [
        { label: "Flexure", value: `${n3(check(beam, "flexure").demand)} / ${n3(check(beam, "flexure").capacity)} kip-ft · ${pct(check(beam, "flexure").dcr)}` },
        { label: "One-way shear", value: `${n3(check(beam, "one_way_shear").demand)} / ${n3(check(beam, "one_way_shear").capacity)} kip · ${pct(check(beam, "one_way_shear").dcr)}` },
        { label: "Single-layer bar fit", value: `${n3(check(beam, "single_layer_bar_fit").demand)} / ${n3(check(beam, "single_layer_bar_fit").capacity)} in · ${pct(check(beam, "single_layer_bar_fit").dcr)} · governs` },
        { label: "Effective depth", value: `${n3(beam.intermediates.d_in as number)} in` },
      ],
    },
    {
      kind: "shot",
      shotId: "concrete-beam-results",
      caption: `${beam.mark}: bar fit governs at ${pct(check(beam, "single_layer_bar_fit").dcr)}, ahead of shear and flexure.`,
    },
    {
      kind: "paragraph",
      text: "A passing flexure card does not finish the section. The summary also carries minimum and maximum longitudinal reinforcement, bar fit, minimum shear reinforcement, and stirrup spacing. Detailed mode expands the same checks into a table with natural units and code references. Scope & limitations names deferred service deflection, crack control, development and splices, durability, fire, seismic detailing, and the geometries outside the rectangular one-layer model.",
    },

    { kind: "heading", level: 2, text: "Strip / Continuous Footing", id: "strip" },
    {
      kind: "paragraph",
      text: `The strip sheet models one foot of a concentric wall footing. ${strip.mark} is 4 ft wide and 18 in thick under an 8 in wall, with D 5 and L 2 kip/ft, 2 ksf allowable bearing, and transverse #8 bars at 8 in. The service pressure is 1.75 ksf, so bearing is ${pct(check(strip, "soil_bearing").dcr)} and governs. The strength solve then reports one-way shear 1.054/16.507 kip/ft and transverse flexure 3.194/72.675 kip-ft/ft.`,
    },
    {
      kind: "shot",
      shotId: "continuous-footing-results",
      caption: `${strip.mark}: service bearing and the strength checks for one transverse foot of footing.`,
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Dead load carries the net convention",
      body: "The engine does not generate footing self-weight, overburden, or buoyancy. The Dead line load label therefore says to include the applicable net footing/soil effect, using a bearing convention compatible with the project's geotechnical allowable.",
    },

    { kind: "heading", level: 2, text: "Pad Footing", id: "pad" },
    {
      kind: "paragraph",
      text: `The pad sheet models a concentric interior column. ${pad.mark} is 12 × 12 ft and 30 in thick under a 24 × 24 in column. D is 541 kip and L is 194 kip; the entered allowables are 4 ksf for D and 5.6 ksf for D+L. The engine keeps those service comparisons separate, then derives the factored soil pressure for the concrete checks.`,
    },
    {
      kind: "values",
      caption: `${pad.mark} · service and strength results.`,
      rows: [
        { label: "Bearing · D", value: `${n3(check(pad, "soil_bearing_dead").demand)} / ${n3(check(pad, "soil_bearing_dead").capacity)} ksf · ${pct(check(pad, "soil_bearing_dead").dcr)} · governs` },
        { label: "Bearing · D+L", value: `${n3(check(pad, "soil_bearing_dead_live").demand)} / ${n3(check(pad, "soil_bearing_dead_live").capacity)} ksf · ${pct(check(pad, "soil_bearing_dead_live").dcr)}` },
        { label: "Punching shear", value: `${n3(check(pad, "two_way_punching_shear").demand)} / ${n3(check(pad, "two_way_punching_shear").capacity)} kip · ${pct(check(pad, "two_way_punching_shear").dcr)}` },
        { label: "Flexure X", value: `${n3(check(pad, "footing_flexure_x").demand)} / ${n3(check(pad, "footing_flexure_x").capacity)} kip-ft · ${pct(check(pad, "footing_flexure_x").dcr)}` },
      ],
    },
    {
      kind: "shot",
      shotId: "pad-footing-results",
      caption: `${pad.mark}: the dead-only service bearing check governs at ${pct(check(pad, "soil_bearing_dead").dcr)}.`,
    },
    {
      kind: "paragraph",
      text: "The pad record also prints one-way shear in X and Y, punching, flexure and provided reinforcement in both directions, and column bearing. It does not model eccentric or biaxial soil pressure, sliding, settlement, punching reinforcement, or dowel development. Those exclusions remain visible in the record and export; changing Quick to Detailed changes density, not the checks performed.",
    },

    { kind: "related", topics: ["steel-cfs-sheets", "masonry-sheets", "section-properties", "linking-reactions"] },
  ],
};
